/**
 * A table, driven by column definitions.
 *
 * Not a table library: every admin grid in this app is the same nine things
 * (scroll, sticky header, right-aligned numbers, an actions column, a clickable
 * row, loading, empty, error, pager), and a generic component over those is
 * smaller than the configuration a library would need for the same result.
 *
 * Row click and an actions column are mutually exclusive in practice: a row that
 * navigates must not also contain buttons, or a click on a button becomes
 * ambiguous. `onRowClick` is only passed by the screens whose rows have no
 * controls of their own.
 *
 * On phones (<=640px) the table renders as stacked cards: the header row is
 * visually hidden and every cell shows its column's label (`Column.label`, or the
 * header when that is a string). The first column is the card's title and the
 * actions column sits at the bottom, both unlabelled - unless the first column is
 * a value rather than an identifier (a date, a rank), which sets `inline`.
 */

import type { ReactNode } from "react";

export type Column<T> = {
  /** Stable identity for React; must be unique within the table. */
  key: string;
  header: ReactNode;
  /** The card-layout label on phones, for a column whose `header` is not a string. */
  label?: string;
  /** Keeps a first column labelled on phones instead of making it the card title. */
  inline?: boolean;
  render: (row: T) => ReactNode;
  align?: "left" | "right";
  /** Applies the tabular-figures treatment. */
  numeric?: boolean;
};

function columnLabel<T>(column: Column<T>): string | undefined {
  if (column.label) return column.label;
  return typeof column.header === "string" && column.header.trim() ? column.header : undefined;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  caption,
}: {
  columns: Array<Column<T>>;
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Screen-reader summary. The visible equivalent is the pager's count. */
  caption?: string;
}) {
  return (
    <div className="table-wrap">
      <table className={`table table--cards ${onRowClick ? "table--clickable" : ""}`}>
        {caption ? <caption className="text-small text-meta" style={{ textAlign: "left", padding: "0 16px 8px" }}>{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={column.align === "right" ? { textAlign: "right" } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              // A clickable row is announced as a button, and Enter activates it,
              // so the grid is usable without a mouse.
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === "Enter") onRowClick(row);
                    }
                  : undefined
              }
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={column.numeric ? "table__num" : undefined}
                  // On phones the header row is hidden and each cell carries its
                  // own label (see `.table--cards` in global.css).
                  data-label={columnLabel(column)}
                  data-inline={column.inline ? "" : undefined}
                  style={column.align === "right" && !column.numeric ? { textAlign: "right" } : undefined}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The two-line cell used in almost every table: a name over a smaller detail. */
export function PrimaryCell({ title, detail }: { title: ReactNode; detail?: ReactNode }) {
  return (
    <>
      <div className="table__strong">{title}</div>
      {detail ? <div className="table__sub">{detail}</div> : null}
    </>
  );
}

/** A row of actions, right-aligned so the column reads as a single edge. */
export function Actions({ children }: { children: ReactNode }) {
  return <div className="table__actions">{children}</div>;
}
